import { ChangeEvent, ReactNode, useState } from "react";
import { Trans, useTranslation } from "next-i18next";
import {
  SlideToggle,
  Listbox,
  Form,
  Combobox,
  Button,
} from "@talladega/components";
import { Controller, UseFormReturn } from "react-hook-form";
import { vehicleValidation } from "@talladega/helpers";
import { LookupVehicle } from "@modules/vehicle/types/VehicleLookupResponse";
import { NameId } from "@common/types/NameId";
import { Province } from "@talladega/types";
import { useNumberMask } from "@common/hooks/useMask";
import useDebounce from "@common/hooks/useDebounce";
import { useVehicleLookupByName } from "@modules/vehicle/hooks/useVehicleLookup";
import useProvincesStatusBySourceList from "@modules/purchase/hooks/useProvincesStatusBySourceList";
import { provinceFilters } from "@modules/purchase/hooks/useEligibleProvinces";
import { VinInput } from "@common/components/form/vehicle";
import { useRouter } from "next/router";
import { TranslationKeys } from "src/@types/i18next";
import { hotjar, Event as HotjarEvent } from "@modules/core/hotjar/types";
import { VEHICLE_NAME_LOOKUP_DEBOUNCE_TIMER } from "@talladega/constants";
import LookupEnum from "../enums/TradeInLookupEnum";
import TradeInSourceName from "../enums/TradeInSourceName";

export interface TradeFindVehicleFormInput {
  vehicle: LookupVehicle;
  vin: string;
  mileage: string;
  dealerId?: NameId;
  province?: Province & { isEnabled: boolean };
}

type Props = {
  form: UseFormReturn<TradeFindVehicleFormInput>;
  button?: ReactNode;
  defaultLookup?: LookupEnum;
  onEnterVehicleManuallyClick?: () => void;
  themeClassNames?: string;
};

const TradeFindVehicleForm = ({
  button,
  form,
  defaultLookup,
  onEnterVehicleManuallyClick,
  themeClassNames,
}: Props) => {
  const { t } = useTranslation(["tradeInPage", "common"]);
  const router = useRouter();
  const locale = router.locale || "en";
  const { maskPipe: maskNumberPipe } = useNumberMask();
  const [tradeInLookUpType, setTradeInLookUpType] = useState<LookupEnum>(
    defaultLookup ?? LookupEnum.VehicleName
  );
  const [selectedVehicle, setSelectedVehicle] = useState<LookupVehicle>();
  const [mileageValue, setMileageValue] = useState<number | null>();
  const [vehicleNameQuery, setVehicleNameQuery] = useState<string>("");
  const debouncedQuery = useDebounce(
    vehicleNameQuery,
    VEHICLE_NAME_LOOKUP_DEBOUNCE_TIMER
  );
  const { vehicles: vehiclesFound } = useVehicleLookupByName(
    debouncedQuery,
    "appraisal-throttle"
  );

  const {
    register,
    control,
    formState: { errors },
    watch,
  } = form;

  const provincesList = useProvincesStatusBySourceList(
    TradeInSourceName.Throttle,
    provinceFilters.isIcoEnabled
  );

  return (
    <div className="flex flex-col items-center p-8 pb-6 w-full bg-white rounded-[1rem]">
      <div className="w-full">
        <SlideToggle
          leftLabel={t("common:vehicle_details")}
          leftValue={LookupEnum.VehicleName}
          rightLabel={t("common:vin")}
          rightValue={LookupEnum.Vin}
          value={tradeInLookUpType}
          onChange={setTradeInLookUpType}
          rounded="md"
          locale={locale}
        />
      </div>
      <form className="grid grid-cols-1 gap-4 w-full pb-10">
        {tradeInLookUpType === LookupEnum.Vin && (
          <div className="w-full">
            <VinInput
              name="vin"
              themeClassNames={themeClassNames}
              showWhereIsMyVin
              register={register}
              errors={errors}
              required
            />
          </div>
        )}

        {tradeInLookUpType === LookupEnum.VehicleName && (
          <div className="w-full">
            <Controller
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("common:vehicle_description_required"),
                },
              }}
              name="vehicle"
              render={({ field: { onChange, ref } }) => (
                <Combobox
                  label={t("common:year_make_model_trim")}
                  items={vehiclesFound}
                  keyOption={(item) => item.externalId || ""}
                  displayOption={(item) => item?.name || ""}
                  displayButton={(item) => item?.name || ""}
                  valueOption={(item) => item}
                  selectedValue={selectedVehicle}
                  status={errors.vehicle ? "error" : "default"}
                  feedback={errors.vehicle?.message}
                  onChange={(selected) => {
                    setSelectedVehicle(selected);
                    onChange(selected);
                  }}
                  onInputChange={(value) => {
                    setVehicleNameQuery(value);
                  }}
                  hideDropdownBtn
                  inputRef={ref}
                  itemsTextDisplay="full"
                />
              )}
            />
            <div className="flex items-center text-sm">
              <Trans
                t={t}
                i18nKey="tradeInPage:cant_find_vehicle_enter_manually"
                components={{
                  a: (
                    <Button
                      type="button"
                      buttonStyle="borderless"
                      fill="link"
                      spacing="no-padding"
                      className="text-primary underline hover:no-underline text-sm"
                      onClick={() => {
                        if (hotjar.initialized()) {
                          hotjar.event(HotjarEvent.IcoVehicleManualSearch);
                        }
                        onEnterVehicleManuallyClick?.();
                      }}
                    />
                  ),
                }}
              />
            </div>
          </div>
        )}

        <div className="w-full">
          <Form.Input
            id="mileage"
            type="text"
            placeholder={t("tradeInPage:mileage_placeholder")}
            maxLength={vehicleValidation.odometer.maxLength}
            defaultValue={mileageValue || undefined}
            label={t("tradeInPage:km_mileage")}
            autoComplete="on"
            feedback={errors.mileage?.message}
            status={errors.mileage ? "error" : "default"}
            {...register("mileage", {
              required: {
                value: true,
                message: t("tradeInPage:mileage_required"),
              },
              maxLength: vehicleValidation.odometer.maxLength,
              min: {
                value: 1,
                message: t("tradeInPage:mileage_required"),
              },
              onChange: (e: ChangeEvent<HTMLInputElement>) => {
                const maskedVal = maskNumberPipe(e.target.value);
                e.target.value = maskedVal;
                setMileageValue(maskedVal);
              },
            })}
          />
        </div>

        <Controller
          control={control}
          rules={{
            required: {
              value: true,
              message: t("common:province_required"),
            },
          }}
          name="province"
          render={({ field: { onChange, ref } }) => (
            <Listbox
              items={provincesList}
              label={t("common:province")}
              keyOption={(item) => item.abbr}
              displayOption={(item) =>
                item.isEnabled
                  ? t(item.name as TranslationKeys["common"])
                  : `${t(item.name as TranslationKeys["common"])} - ${t(
                      "common:coming_soon"
                    )}`
              }
              selectedItem={watch("province")}
              displayButton={(item) =>
                item?.name != null
                  ? t(item.name as TranslationKeys["common"])
                  : t("common:select_province")
              }
              feedback={
                errors?.province?.type === "required"
                  ? t("common:province_required")
                  : ""
              }
              valueOption={(item) => item}
              disabledOption={(item) => !item.isEnabled}
              onChange={onChange}
              status={errors.province ? "error" : "default"}
              defaultButtonClassName="text-slate-500"
              buttonRef={ref}
            />
          )}
        />
      </form>

      {button}
    </div>
  );
};

export default TradeFindVehicleForm;
